import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Pencil, Trash2, Plus, X, UserPlus, Crown } from "lucide-react";
import GoalsSection from "./GoalsSection";
import { SkillPicker } from "./SkillPicker";
import { ReceivedWorkProducts } from "./ReceivedWorkProducts";
import { EmployeeEditSheet } from "./EmployeeEditSheet";
import { useAuth } from "../context/AuthContext";
import { transferTargets } from "../lib/connections";

const SYSTEM_TITLES = {
  s2: "Systeem II · Coördinatie",
  s3: "Systeem III · Controle",
  s4: "Systeem IV · Intelligentie",
  s5: "Systeem V · Beleid / Identiteit",
};

function EmployeeForm({ initial, onSave, onCancel, strategies }) {
  const [f, setF] = useState(initial || { name: "", role: "", email: "", phone: "", notes: "" });
  const [skills, setSkills] = useState(initial?.skill_ids || []);
  return (
    <div className="border border-zinc-900 p-3 space-y-2 bg-zinc-50" data-testid="employee-form">
      <Input placeholder="Naam" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-none border-zinc-400" data-testid="employee-name-input" />
      <Input placeholder="Rol / functie" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="rounded-none border-zinc-400" data-testid="employee-role-input" />
      <Input placeholder="E-mail" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-none border-zinc-400" data-testid="employee-email-input" />
      <Input placeholder="Telefoonnummer" value={f.phone || ""} onChange={(e) => setF({ ...f, phone: e.target.value })} className="rounded-none border-zinc-400" data-testid="employee-phone-input" />
      <Textarea placeholder="Notities" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="rounded-none border-zinc-400" data-testid="employee-notes-input" />
      {strategies && (
        <div className="pt-2 border-t border-zinc-300">
          <SkillPicker strategies={strategies} value={skills} onChange={setSkills} />
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" className="rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={() => onSave(f, skills)} data-testid="save-employee-button">
          Opslaan
        </Button>
        <Button size="sm" variant="outline" className="rounded-none" onClick={onCancel}>
          Annuleren
        </Button>
      </div>
    </div>
  );
}

export default function EmployeeDrawer({ open, onOpenChange, systemKey, unitId, node, orgId, canEdit, canGoalS3, canGoalOps, orgEmployees, onRefresh, refreshEmployees, strategies, myEmployeeId, transfers, reloadTransfers }) {
  const [adding, setAdding] = useState(false);
  const [sheetEmp, setSheetEmp] = useState(null);
  const [assignId, setAssignId] = useState("");
  const [renaming, setRenaming] = useState(false);
  const [unitName, setUnitName] = useState("");
  const [sysEditing, setSysEditing] = useState(false);
  const [sysName, setSysName] = useState("");
  const [newUnitName, setNewUnitName] = useState("");

  const isUnitSystem = systemKey === "s2" || systemKey === "s4";
  const [selectedUnitId, setSelectedUnitId] = useState(null);

  const units = isUnitSystem && node ? (systemKey === "s2" ? node.system2_units : node.system4_units) : [];

  const current = (() => {
    if (!node) return { employees: [], unit: null };
    if (systemKey === "s3") return { employees: node.system3_employees, unit: null };
    if (systemKey === "s5") return { employees: node.system5_employees, unit: null };
    const unit = units.find((u) => u.id === selectedUnitId) || units[0] || null;
    return { employees: unit ? unit.employees : [], unit };
  })();

  useEffect(() => {
    setAdding(false);
    setSheetEmp(null);
    setAssignId("");
    setRenaming(false);
    setSysEditing(false);
    setSelectedUnitId(unitId || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, systemKey, unitId]);

  useEffect(() => {
    setUnitName(current.unit ? current.unit.name : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.unit?.id, open]);

  const assignNew = async (f) => {
    if (!f.name.trim()) return toast.error("Naam is verplicht");
    await api.post(`/nodes/${node.id}/systems/${systemKey}/employees`, { unit_id: current.unit?.id, new_employee: f });
    setAdding(false);
    toast.success("Medewerker toegevoegd");
    onRefresh();
    refreshEmployees();
  };

  const assignExisting = async () => {
    if (!assignId) return;
    await api.post(`/nodes/${node.id}/systems/${systemKey}/employees`, { unit_id: current.unit?.id, employee_id: assignId });
    setAssignId("");
    onRefresh();
  };

  const unassign = async (empId) => {
    const q = current.unit ? `?unit_id=${current.unit.id}` : "";
    await api.delete(`/nodes/${node.id}/systems/${systemKey}/employees/${empId}${q}`);
    onRefresh();
  };

  const canEditPeople = ["superadmin", "consultant", "org_admin"].includes(useAuth().user.role);

  const saveSystemName = async () => {
    if (!sysName.trim()) return toast.error("Naam mag niet leeg zijn");
    await api.patch(`/nodes/${node.id}/systems/${systemKey}/name`, { name: sysName.trim() });
    setSysEditing(false);
    toast.success("Naam opgeslagen");
    onRefresh();
  };

  const saveUnitName = async () => {
    if (!unitName.trim()) return toast.error("Een afdeling moet een naam hebben");
    await api.patch(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}`, { name: unitName.trim() });
    setRenaming(false);
    onRefresh();
  };

  const addUnit = async () => {
    if (!newUnitName.trim()) return toast.error("Een afdeling moet een naam hebben");
    const { data } = await api.post(`/nodes/${node.id}/units/${systemKey}`, { name: newUnitName.trim() });
    const list = systemKey === "s2" ? data.node.system2_units : data.node.system4_units;
    setSelectedUnitId(list[list.length - 1].id);
    setNewUnitName("");
    setAdding(true);
    toast.success("Afdeling toegevoegd — voeg nu medewerkers toe");
    onRefresh();
  };

  const deleteUnit = async () => {
    await api.delete(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}`);
    toast.success("Afdeling verwijderd");
    onRefresh();
  };

  const toggleManager = async (empId) => {
    const next = current.unit.manager_id === empId ? "" : empId;
    try {
      await api.put(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}/manager`, { employee_id: next });
      toast.success(next ? "Manager van de afdeling benoemd" : "Manager-rol verwijderd");
      onRefresh();
    } catch {
      toast.error("Manager instellen mislukt");
    }
  };

  const assignedIds = new Set(current.employees.map((e) => e.id));
  const available = (orgEmployees || []).filter((e) => !assignedIds.has(e.id));

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="employee-drawer">
        <SheetHeader>
          <SheetTitle className="font-grotesk tracking-tight">{SYSTEM_TITLES[systemKey]}</SheetTitle>
        </SheetHeader>

        {(systemKey === "s3" || systemKey === "s5") && (
          <div className="mt-4 border border-zinc-900 p-3 bg-zinc-50">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Naam {systemKey === "s3" ? "Systeem III" : "Systeem V"}</div>
            {sysEditing ? (
              <div className="flex gap-2">
                <Input value={sysName} onChange={(e) => setSysName(e.target.value)} className="rounded-none border-zinc-400 h-8" data-testid="system-name-input" />
                <Button size="sm" className="rounded-none bg-zinc-900 h-8" onClick={saveSystemName} data-testid="save-system-name-button">
                  OK
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="font-grotesk font-semibold">{systemKey === "s3" ? node.system3_name : node.system5_name}</span>
                {canEdit && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => {
                      setSysName(systemKey === "s3" ? node.system3_name : node.system5_name);
                      setSysEditing(true);
                    }}
                    data-testid="rename-system-button"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {isUnitSystem && (
          <div className="mt-4 border border-zinc-900 p-3 bg-zinc-50">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Afdeling</div>
            {units.length === 0 ? (
              <p className="text-sm text-zinc-500" data-testid="no-units-hint">
                Nog geen afdelingen. Medewerkers horen in Systeem {systemKey === "s2" ? "II" : "IV"} altijd bij een afdeling — geef hieronder een naam en maak er eerst één aan; daarna kun je direct medewerkers toevoegen.
              </p>
            ) : renaming ? (
              <div className="flex gap-2">
                <Input value={unitName} onChange={(e) => setUnitName(e.target.value)} className="rounded-none border-zinc-400 h-8" data-testid="unit-name-input" />
                <Button size="sm" className="rounded-none bg-zinc-900 h-8" onClick={saveUnitName} data-testid="save-unit-button">
                  OK
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Select value={current.unit?.id || ""} onValueChange={setSelectedUnitId}>
                  <SelectTrigger className="rounded-none border-zinc-400 h-8" data-testid="unit-select">
                    <SelectValue placeholder="Kies afdeling" />
                  </SelectTrigger>
                  <SelectContent>
                    {units.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {canEdit && current.unit && (
                  <>
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setRenaming(true)} data-testid="rename-unit-button">
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0 text-red-600" onClick={deleteUnit} data-testid="delete-unit-button">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            )}
            {canEdit && (
              <div className="flex gap-2 mt-2">
                <Input
                  value={newUnitName}
                  onChange={(e) => setNewUnitName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addUnit()}
                  placeholder="Naam nieuwe afdeling"
                  className="rounded-none border-zinc-400 h-8"
                  data-testid="new-unit-name-input"
                />
                <Button size="sm" variant="outline" className="rounded-none h-8 shrink-0" onClick={addUnit} data-testid="add-unit-button">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Afdeling
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="mt-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Medewerkers{current.unit ? ` · ${current.unit.name}` : ""} ({current.employees.length})
          </div>
          <div className="space-y-2">
            {current.employees.map((e) => (
              <div key={e.id} className="border border-zinc-300 p-3 hover:border-zinc-900 transition-colors" data-testid="employee-card">
                  <div className="flex items-start justify-between gap-2">
                    <button className="min-w-0 flex-1 text-left" onClick={() => setSheetEmp(e)} data-testid="open-employee-sheet-button">
                      <div className="font-grotesk font-semibold flex items-center gap-2">{e.name}{current.unit?.manager_id === e.id && <span className="font-mono text-[10px] uppercase tracking-widest bg-zinc-900 text-white px-1.5 py-0.5" data-testid="unit-manager-badge">Manager</span>}</div>
                      {e.role && <div className="text-sm text-zinc-600">{e.role}</div>}
                      {e.email && <div className="text-xs font-mono text-zinc-500 truncate">{e.email}</div>}
                      {e.phone && <div className="text-xs font-mono text-zinc-500 truncate">{e.phone}</div>}
                      {e.notes && <div className="text-xs text-zinc-500 mt-1">{e.notes}</div>}
                      {(e.skill_ids || []).length > 0 && <div className="text-[11px] font-mono text-zinc-400 mt-1">{e.skill_ids.length} vaardigheden</div>}
                    </button>
                    {canEdit && (
                      <div className="flex gap-1 shrink-0">
                        {current.unit && (
                          <Button size="icon" variant="ghost" className={`h-7 w-7 ${current.unit.manager_id === e.id ? "text-zinc-900" : "text-zinc-400"}`} onClick={() => toggleManager(e.id)}
                            title={current.unit.manager_id === e.id ? "Niet meer manager van deze afdeling" : "Benoem tot manager van deze afdeling"} data-testid="toggle-unit-manager-button">
                            <Crown className="h-4 w-4" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500" onClick={() => unassign(e.id)} title="Van systeem halen" data-testid="unassign-employee-button">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
              </div>
            ))}
            {current.employees.length === 0 && <p className="text-sm text-zinc-400">Nog geen medewerkers toegewezen.</p>}
          </div>
        </div>

        {canEdit && (!isUnitSystem || current.unit) && (
          <div className="mt-5 space-y-3">
            {adding ? (
              <EmployeeForm onSave={assignNew} onCancel={() => setAdding(false)} />
            ) : (
              <Button className="w-full rounded-none bg-zinc-900 hover:bg-zinc-700" onClick={() => setAdding(true)} data-testid="add-employee-button">
                <UserPlus className="h-4 w-4 mr-2" /> Nieuwe medewerker
              </Button>
            )}

            {available.length > 0 && (
              <div className="flex gap-2">
                <Select value={assignId} onValueChange={setAssignId}>
                  <SelectTrigger className="rounded-none border-zinc-400" data-testid="assign-existing-select">
                    <SelectValue placeholder="Bestaande medewerker…" />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} {e.role ? `· ${e.role}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" className="rounded-none" onClick={assignExisting} data-testid="assign-existing-button">
                  Koppel
                </Button>
              </div>
            )}
          </div>
        )}
        {systemKey === "s3" && (
          <GoalsSection
            title="Hoofddoelen"
            subtitle="Alleen Systeem V stelt hoofddoelen"
            goals={node.system3_goals}
            canGive={canGoalS3}
            testid="s3-goals-section"
            onAdd={async (t) => {
              await api.post(`/nodes/${node.id}/goals/s3`, { text: t });
              onRefresh();
            }}
            onDelete={async (id) => {
              await api.delete(`/nodes/${node.id}/goals/s3/${id}`);
              onRefresh();
            }}
            onEdit={async (id, t) => {
              await api.patch(`/nodes/${node.id}/goals/s3/${id}`, { text: t });
              onRefresh();
            }}
          />
        )}

        {isUnitSystem && current.unit && (
          <GoalsSection
            title="Subdoelen afdeling"
            subtitle="Gesteld door Systeem III · gekoppeld aan een hoofddoel"
            goals={current.unit.goals}
            canGive={canGoalOps}
            requiresParent
            parentOptions={node.system3_goals}
            testid="unit-goals-section"
            onAdd={async (t, pid) => {
              await api.post(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}/goals`, { text: t, parent_goal_id: pid });
              onRefresh();
            }}
            onDelete={async (id) => {
              await api.delete(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}/goals/${id}`);
              onRefresh();
            }}
            onEdit={async (id, t, pid) => {
              await api.patch(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}/goals/${id}`, { text: t, parent_goal_id: pid });
              onRefresh();
            }}
            strategies={strategies}
            canLink={canGoalOps || current.unit.employees.some((e) => e.id === myEmployeeId)}
            canLoadTaker={canEditPeople || current.unit.employees.some((e) => e.id === myEmployeeId)}
            onLoad={async (gid, set, v) => {
              await api.put(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}/goals/${gid}/load`, { set, ...v });
              onRefresh();
            }}
            memberSkills={new Set(current.unit.employees.flatMap((e) => e.skill_ids || []))}
            onLinkStrategies={async (gid, ids) => {
              await api.put(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}/goals/${gid}/strategies`, { strategy_ids: ids });
              onRefresh();
            }}
            onProgress={async (gid, wpId, pct, ok) => {
              await api.put(`/nodes/${node.id}/units/${systemKey}/${current.unit.id}/goals/${gid}/progress`, { work_product_id: wpId, percent: pct, approved: ok });
              onRefresh();
            }}
            transferCtx={{
              source: { node_id: node.id, system: systemKey, unit_id: current.unit.id },
              targets: transferTargets(node, systemKey),
              transfers: transfers || [],
              reload: reloadTransfers,
              refreshGoals: onRefresh,
            }}
          />
        )}
        {isUnitSystem && current.unit && (
          <ReceivedWorkProducts
            transfers={(transfers || []).filter((t) => t.to.node_id === node.id && t.to.unit_id === current.unit.id)}
            canReview={canGoalOps || current.unit.employees.some((e) => e.id === myEmployeeId)}
            reload={reloadTransfers}
          />
        )}
      </SheetContent>
      <EmployeeEditSheet
        employee={sheetEmp}
        strategies={strategies || []}
        canEdit={canEditPeople}
        onClose={() => setSheetEmp(null)}
        onSaved={() => {
          onRefresh();
          refreshEmployees();
        }}
      />
    </Sheet>
  );
}
